import ErrorMessage from '@shared/components/ErrorMessage/ErrorMessage';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Clock } from 'assets/icons/Clock.svg';
import DayInput from 'components/DateInput/DayInput';
import ModalFooter from 'components/ModalFooter';
import constants from 'Constants';
import DescriptionArea from 'shared/components/ScheduleVideoMeetingModal/DescriptionArea';
import DurationTimePicker from 'shared/components/ScheduleVideoMeetingModal/DurationTimePicker';
import RecurrenceSelect from 'shared/components/ScheduleVideoMeetingModal/RecurrenceSelect';
import TimezoneSelect from 'shared/components/ScheduleVideoMeetingModal/TimezoneSelect';

import GroupChatToggleField from './GroupChatToggleField';
import ScheduleMeetingAliasField from './ScheduleMeetingAliasField';
import ScheduleMeetingRoomNameField from './ScheduleMeetingRoomNameField';
import StartTimePicker, { buildStartTimeOptions } from './StartTimePicker';
import { ScheduleMeetingFormValues } from './types';
import { SlugAvailabilityStatus } from './useSlugAvailability';
import { combineDateAndTime, DEFAULT_DURATION_MS } from './utils';

// First selectable end-time slot relative to the start (matches DurationTimePicker's 15-min step).
const FIRST_END_SLOT_MS = 15 * 60 * 1000;

interface ScheduleMeetingStep1Props {
  messengerRoomId?: string;
  isValid: boolean;
  isStartInPast: boolean;
  isEndBeforeStart: boolean;
  slugAvailability: SlugAvailabilityStatus;
  onNext: () => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  hideGroupChatToggle?: boolean;
}

const ScheduleMeetingStep1 = ({
  messengerRoomId,
  isValid,
  isStartInPast,
  isEndBeforeStart,
  slugAvailability,
  onNext,
  onCancel,
  isSubmitting,
  hideGroupChatToggle,
}: ScheduleMeetingStep1Props) => {
  const { t } = useTranslation();
  const {
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<ScheduleMeetingFormValues>();

  const startDate = watch('startDate');
  const startTime = watch('startTime');
  const timezone = watch('timezone');
  const showEndSection = startTime !== null;

  const [endTimeOpenSignal, setEndTimeOpenSignal] = useState(0);

  // Current moment in the selected timezone. Used to derive both the minimum
  // selectable date (calendar day) and the minimum selectable start time.
  const tzNow = moment().utcOffset(timezone * 60);
  // Minimum selectable start date = start of "today" in the selected timezone,
  // expressed as browser-local midnight so react-day-picker compares calendar days correctly.
  const minStartDate = moment(tzNow.format('YYYY-MM-DD'), 'YYYY-MM-DD').toDate();

  // On startTime selection or change:
  // - If endTime is null (first selection): initialise endDate + endTime to startTime + 1hr,
  //   advancing endDate by 1 day when the default +1hr window crosses midnight (startTime >= 23:00).
  // - If endTime is already set: when startTime + 1hr crosses midnight and endDate is still on
  //   the same day, advance endDate by 1 day so the end-time picker always has valid options.
  useEffect(() => {
    if (startTime === null) return;
    const endTime = getValues('endTime');

    const newEndTime = new Date(startTime.getTime() + DEFAULT_DURATION_MS);
    const rollsOver =
      newEndTime.getDate() !== startTime.getDate() ||
      newEndTime.getMonth() !== startTime.getMonth() ||
      newEndTime.getFullYear() !== startTime.getFullYear();

    if (endTime === null) {
      const currentEndDate = getValues('endDate');
      if (currentEndDate === null) {
        const baseDate = getValues('startDate')!;
        const newEndDate = rollsOver ? new Date(baseDate.getTime() + 24 * 60 * 60 * 1000) : baseDate;
        setValue('endDate', newEndDate, { shouldValidate: true });
      }
      setValue('endTime', newEndTime, { shouldValidate: true });
    } else {
      const currentEndDate = getValues('endDate');
      if (rollsOver && currentEndDate && moment(currentEndDate).isSame(moment(startTime), 'day')) {
        setValue('endDate', new Date(currentEndDate.getTime() + 24 * 60 * 60 * 1000), {
          shouldValidate: true,
        });
      }
    }
  }, [startTime, setValue, getValues]);

  return (
    <>
      <div className="flex flex-1 flex-col gap-4 min-h-0 overflow-y-auto px-4 py-4">
        {/* Name */}
        <ScheduleMeetingRoomNameField />

        {/* Description */}
        <Controller
          name="description"
          control={control}
          render={({ field: { onChange, value } }) => (
            <>
              <DescriptionArea
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={t('schedule_meeting.description_placeholder')}
                maxLength={constants.MAX_TOPIC_LENGTH + 1}
              />
              {errors.description?.message && (
                <div role="alert" aria-live="polite" className="pl-11">
                  <ErrorMessage message={t(errors.description.message)} />
                </div>
              )}
            </>
          )}
        />

        {/* Slug — hidden when opened from a Messenger room (#8032 Part A); slug is still
            auto-generated and submitted, the field is purely a UI affordance. */}
        {!messengerRoomId && (
          <div className="pl-3">
            <ScheduleMeetingAliasField slugAvailability={slugAvailability} />
          </div>
        )}
        {/* Group chat toggle — hidden for messenger rooms (auto-linked) and for guests (no messenger access) */}
        {!messengerRoomId && !hideGroupChatToggle && <GroupChatToggleField />}

        <div className="flex gap-2">
          {/* Start date + time */}
          <div className="px-3">
            <Clock className="stroke-gray-60 h-6 w-6 shrink-0 stroke-1.5" />
          </div>
          <div className="flex flex-col gap-4 w-full">
            <div className="flex flex-col gap-2">
              <span className="text-size-sm font-semibold text-gray-80">
                {t('messenger.schedule_meeting_start_label')}
              </span>
              <div className="flex gap-5">
                <div className="flex w-full flex-col gap-2">
                  <div className="flex flex-wrap gap-2">
                    <Controller
                      name="startDate"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <DayInput
                          name="startDate"
                          value={value}
                          onChange={(d) => {
                            onChange(d ?? null);

                            // #8112 — When switching to today with a stale past startTime, snap it
                            // forward to the first valid future slot so the form does not show the
                            // "start in past" error. Mirrors the same tzNow cutoff the picker uses
                            // for disabledBeforeTime above.
                            if (d) {
                              const isSwitchingToToday = moment(d).format('YYYY-MM-DD') === tzNow.format('YYYY-MM-DD');
                              if (isSwitchingToToday) {
                                const cutoff = new Date(new Date().setHours(tzNow.hours(), tzNow.minutes(), 0, 0));
                                const cutoffMinutes = cutoff.getHours() * 60 + cutoff.getMinutes();
                                const currentStartTime = getValues('startTime');
                                const storedMinutes = currentStartTime
                                  ? currentStartTime.getHours() * 60 + currentStartTime.getMinutes()
                                  : -1;
                                if (currentStartTime && storedMinutes <= cutoffMinutes) {
                                  const validOptions = buildStartTimeOptions(cutoff);
                                  setValue('startTime', validOptions.length > 0 ? validOptions[0].time : null, {
                                    shouldValidate: true,
                                  });
                                }
                              }
                            }

                            // If start date advances past end date, forward end date to match
                            const currentEndDate = getValues('endDate');
                            if (d && currentEndDate && moment(currentEndDate).isBefore(moment(d), 'day')) {
                              setValue('endDate', d, { shouldValidate: true });
                            }
                          }}
                          placeholder={t('schedule_meeting.date_placeholder')}
                          className="min-w-[140px] flex-1"
                          disabledBeforeDate={minStartDate}
                        />
                      )}
                    />
                    <Controller
                      name="startTime"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <StartTimePicker
                          name="startTime"
                          value={value}
                          // #8070 BUG-001 — auto-fill the start date with today (in the selected timezone) the
                          // instant the dropdown opens, before any time is selected. This makes the
                          // disabledBeforeTime IIFE below compute the today cutoff so past slots are excluded on
                          // the very first open. minStartDate is already today-in-tz.
                          onOpen={() => {
                            if (!getValues('startDate')) {
                              setValue('startDate', minStartDate, { shouldValidate: true });
                            }
                          }}
                          onChange={(time) => {
                            // #8070 — defence-in-depth: also fill the start date on selection in case a time is
                            // committed without an open transition firing first. Idempotent (only when empty).
                            if (!getValues('startDate')) {
                              setValue('startDate', minStartDate, { shouldValidate: true });
                            }
                            const prevEndTime = getValues('endTime');
                            onChange(time);
                            // Only auto-open end picker on first selection (when endTime was null)
                            if (prevEndTime === null) {
                              setEndTimeOpenSignal((s) => s + 1);
                            }
                          }}
                          placeholder={t('schedule_meeting.time_placeholder')}
                          disabledBeforeTime={(() => {
                            if (!startDate) return undefined;
                            const isStartDateTodayInTz =
                              moment(startDate).format('YYYY-MM-DD') === tzNow.format('YYYY-MM-DD');
                            if (!isStartDateTodayInTz) return undefined;
                            return new Date(new Date().setHours(tzNow.hours(), tzNow.minutes(), 0, 0));
                          })()}
                        />
                      )}
                    />
                  </div>
                  {isStartInPast && <ErrorMessage message={t('messenger.schedule_meeting_start_in_past')} />}
                </div>
              </div>
            </div>
            {/* End date + time (shown only after startTime is set) */}
            {showEndSection && (
              <div className="flex flex-col gap-2">
                <span className="text-size-sm font-semibold text-gray-80">
                  {t('messenger.schedule_meeting_end_label')}
                </span>
                <div className="flex gap-5">
                  <div className="flex w-full flex-col gap-2">
                    <div className="flex flex-wrap gap-2">
                      <Controller
                        name="endDate"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <DayInput
                            name="endDate"
                            value={value}
                            onChange={(d) => {
                              onChange(d ?? null);

                              // BUG-001 — When moving the end date so the resulting end is at or before
                              // the start, snap the end time forward to the first valid same-day slot
                              // (start + 15 min — the first option the DurationTimePicker offers). Mirrors
                              // the #8112 start-date snap so a stale end time cannot leave end < start.
                              if (d && startDate && startTime) {
                                const currentEndTime = getValues('endTime');
                                const combinedStart = combineDateAndTime(startDate, startTime);
                                const combinedEnd = combineDateAndTime(d, currentEndTime);
                                if (combinedStart && combinedEnd && combinedEnd.getTime() <= combinedStart.getTime()) {
                                  setValue('endTime', new Date(startTime.getTime() + FIRST_END_SLOT_MS), {
                                    shouldValidate: true,
                                  });
                                }
                              }
                            }}
                            placeholder={t('schedule_meeting.date_placeholder')}
                            className="min-w-[140px] flex-1"
                            disabledBeforeDate={startDate ?? undefined}
                          />
                        )}
                      />
                      <Controller
                        name="endTime"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <DurationTimePicker
                            name="endTime"
                            value={value}
                            onChange={onChange}
                            startTime={combineDateAndTime(startDate, startTime)}
                            endDate={watch('endDate')}
                            placeholder={t('schedule_meeting.time_placeholder')}
                            openSignal={endTimeOpenSignal}
                          />
                        )}
                      />
                    </div>
                    {isEndBeforeStart && <ErrorMessage message={t('messenger.schedule_meeting_invalid_time_range')} />}
                  </div>
                </div>
              </div>
            )}
            {/* Timezone */}
            <Controller
              name="timezone"
              control={control}
              render={({ field: { onChange, value } }) => (
                <TimezoneSelect name="timezone" value={value} onChange={onChange} />
              )}
            />
            {/* Recurrence */}
            <div className="flex flex-col gap-2">
              <span className="text-size-sm font-semibold text-gray-80">
                {t('messenger.schedule_meeting_repeat_label')}
              </span>
              <Controller
                name="recurrence"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <RecurrenceSelect name="recurrence" value={value} onChange={onChange} />
                )}
              />
            </div>
          </div>
        </div>
      </div>

      <ModalFooter
        cancelText={t('cancel')}
        successButtonText={messengerRoomId ? t('schedule_meeting.submit_button') : t('next')}
        onCloseClick={onCancel}
        onSubmit={onNext}
        successDisabled={!isValid}
        isLoading={!!isSubmitting}
      />
    </>
  );
};

export default ScheduleMeetingStep1;
